import { call, put } from 'redux-saga/effects';
import { createIntl, createIntlCache } from 'react-intl';
import translations from '~/infra/i18n/locales';
import { getLocale } from '~/utils/getLocale';

import { iActionCreate } from '~/domain/interfaces/redux/clinicalDocs/create';
import { CreateClinicalDocs } from '~/domain/usecases/clinicalDocs/remote/CreateClinicalDocs';
import {
  createSuccess,
  createFailed,
} from '~/data/store/reducer/clinicalDocs/actions/create';

import { AlertMessage } from '~/presentation/components/messages/AlertMessage';

import {
  GetAllClinicalDocs,
  PresignClinicalDocs,
} from '~/domain/usecases/clinicalDocs/remote';
import { Sign } from '~/domain/usecases/deamon/remote';
import { SignClinicalDocs } from '~/domain/usecases/clinicalDocs/redux/SignClinicalDocs';

import { SignFailed } from '~/data/store/reducer/clinicalDocs/actions/sign';

import { makeReduxGetAllClinicalDocs } from '~/main/factories/usecases/clinicalDocs/GetAllClinicalDocsFactory';
import { makeRemoteUpdateClinicalDocsBySign } from '~/main/factories/usecases/clinicalDocs/UpdateClinicalDocsBySignFactory';
import { makeRemoteSignDocument } from '~/main/factories/usecases/daemon/Sign';
import { iStore } from '~/domain/interfaces/models';
import storeDev from '../..';
import { makeRemoteCreateAttachedClinicalDocs } from '~/main/factories/usecases/clinicalDocs/CreateClinicalDocsAttachedFactory';
import { makeRemoteCreateDetachedClinicalDocs } from '~/main/factories/usecases/clinicalDocs/CreateClinicalDocsDetachedFactory';
import { makeRemotePresignClinicalDocs } from '~/main/factories/usecases/clinicalDocs/PresignClinicalDocsDetachedFactory';

const cache = createIntlCache();

const intl = createIntl(
  {
    locale: String(getLocale()),
    messages: translations[getLocale()],
  },
  cache,
);

export function* onCreate(action: iActionCreate) {
  let remoteCreateClinicalDocs;

  if (action.payload.appointment) {
    remoteCreateClinicalDocs = makeRemoteCreateAttachedClinicalDocs();
  } else {
    remoteCreateClinicalDocs = makeRemoteCreateDetachedClinicalDocs();
  }

  try {
    const response: CreateClinicalDocs.Model = yield call(
      remoteCreateClinicalDocs.create,
      action.payload,
    );

    console.log(response);
    if (
      action.payload.certificate !== undefined &&
      action.payload.certificateId !== undefined
    ) {
      try {
        yield SignDoc({
          docId: response.id,
          certificate: action.payload.certificate,
          certificateId: String(action.payload.certificateId),
          appointmentId: action.payload.appointment,
          isVidaas: !!action.payload.signDocWithVidaas,
        });
      } catch (e) {
        console.error(e);
        yield put(SignFailed());
      }
    }

    const signDocWithVidaas = action?.payload?.signDocWithVidaas;
    yield put(
      createSuccess({
        ...response,
        sidebar: action.payload?.sidebar,
        goBackToListDocument: action.payload?.goBackToListDocument,
        signDocWithVidaas: signDocWithVidaas
          ? (id: number) => signDocWithVidaas(id)
          : undefined,
      }),
    );

    const SignLedgerResponse = SignDocumentWithLedger({
      docId: response.id,
      certificate: action.payload.certificate!,
      certificateId: String(action.payload.certificateId),
      appointmentId: action.payload.appointment,
    });
  } catch (e) {
    console.error('error: ', e);

    yield put(createFailed());
  }
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
export function onCreateSuccess() {
  console.log('##oi');
}

export function onCreateFailed() {
  AlertMessage({
    type: 'danger',
    message: intl.formatMessage({ id: 'Falha ao criar documento clínico.' }),
  });
}

function* SignDocumentWithLedger(payload: SignClinicalDocs.Params) {
  const remotePresignClinicalDocs = makeRemotePresignClinicalDocs();
  const remoteSignClinicalDocs = makeRemoteSignDocument();

  if (!payload.docId) throw new Error();

  // presign
  const response: PresignClinicalDocs.Model = yield call(
    remotePresignClinicalDocs.presign,
    {
      docId: payload.docId,
      certificate: payload.certificate,
      signatureSource: 'LEDGERSIGN',
    },
  );

  const responseSignDocument: Sign.Model = yield call(
    remoteSignClinicalDocs.sign,
    {
      certId: payload.certificateId,
      algorithm: 'SHA256',
      dataToSignArray: [
        {
          dataToSign: response.dataToSign,
          key: 1,
        },
      ],
    },
  );

  return responseSignDocument;
}

function* SignDoc(payload: SignClinicalDocs.Params) {
  const remoteSignClinicalDocs = makeRemoteSignDocument();
  const remoteSendSignatureValueBackend = makeRemoteUpdateClinicalDocsBySign();

  const store: iStore = storeDev.getState();
  const { orgId } = store.auth.selectUser;

  if (!payload.docId) throw new Error();

  const responsePresignDoc: PresignClinicalDocs.Model = yield call(
    getDocDelay,
    {
      docId: payload.docId,
      certificate: payload.certificate,
      signatureSource: payload.isVidaas ? 'VIDAAS' : 'LEDGERSIGN',
    },
  );

  const signClinicalDocsBody: Sign.Params = {
    certId: payload.certificateId,
    algorithm: 'SHA256',
    dataToSignArray: [
      {
        dataToSign: responsePresignDoc.dataToSign,
        key: 1,
      },
    ],
  };

  const responseSignDocument: Sign.Model = yield call(
    remoteSignClinicalDocs.sign,
    signClinicalDocsBody,
  );

  yield call(remoteSendSignatureValueBackend.patchBySign, {
    docId: payload.docId,
    signatureValue: responseSignDocument[0].signature,
  });

  yield call(getAllDocDelay, {
    filter: {
      appointment: payload.appointmentId,
      org: orgId,
      enabled: true,
    },
  });

  AlertMessage({
    type: 'success',
    message: intl.formatMessage({ id: 'Documento assinado com sucesso!' }),
  });
}

async function getAllDocDelay(data: GetAllClinicalDocs.Params): Promise<void> {
  const reduxGetAllClinicalDocs = makeReduxGetAllClinicalDocs();

  await new Promise<void>((resolve, reject) => {
    setTimeout(async () => {
      reduxGetAllClinicalDocs.getAll(data?.filter?.appointment ? data : {});
      resolve();
    }, 6000);
  });
}

async function getDocDelay(
  data: PresignClinicalDocs.Params,
): Promise<PresignClinicalDocs.Model> {
  const remotePresignClinicalDocs = makeRemotePresignClinicalDocs();

  const response: PresignClinicalDocs.Model = await new Promise(
    (resolve, reject) => {
      setTimeout(async () => {
        remotePresignClinicalDocs
          .presign(data)
          .then(result => resolve(result))
          .catch(e => reject(e));
      }, 6000);
    },
  );

  console.log('<><><> getDocDelay: ', response);

  return response;
}
